// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";

export interface TitleTypographyProps {
    link?: string | LinkFieldValue;
    onLinkClick?: () => void;
    linkText?: string;
    openInNewTab?: boolean;
    showIcon?: boolean;
    extendedStyles?: TitleTypographyStyles;
}

type Props = TitleTypographyProps;

interface TitleTypographyLink {
    link?: LinkFieldValue;
    onClick?: () => void;
    url?: string;
    openInNewTab?: boolean;
    linkText?: string;
    styles: TitleTypographyStyles;
    showIcon?: boolean;
}

export interface TitleTypographyStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    linkWithArrow?: LinkPresentationProps;
    link?: LinkPresentationProps;
}

export const titleTypographyStyles: TitleTypographyStyles = {};

const TitleLink: FC<TitleTypographyLink> = ({ url, openInNewTab, linkText, styles, onClick, showIcon }) => {
    const linkstyle = showIcon ? styles.linkWithArrow : styles.link;
    return (
        <Link
            href={url}
            onClick={onClick}
            aria-label={linkText}
            target={openInNewTab ? "_blank" : undefined}
            {...linkstyle}
        >
            {linkText}
        </Link>
    );
};

const TitleTypographyLink: FC<TitleTypographyLink> = ({ link, showIcon, ...otherProps }) => {
    const { url } = useGetLink(link ?? { type: "Page", value: "" });
    return <TitleLink showIcon={showIcon} {...otherProps} url={url} />;
};

const TitleTypography: FC<Props> = ({
    openInNewTab,
    linkText,
    extendedStyles,
    children,
    link,
    onLinkClick,
    showIcon,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(titleTypographyStyles, extendedStyles));
    const linkProps = {
        styles,
        openInNewTab,
        linkText,
        showIcon,
    };

    const linkComponent = () => {
        if ((!link && !onLinkClick) || !linkText) {
            return null;
        }
        if (typeof link === "string") {
            return <TitleLink aria-label={linkText} url={link} {...linkProps} />;
        }
        if (link && link.value) {
            return <TitleTypographyLink aria-label={linkText} link={link} {...linkProps} />;
        }
        if (onLinkClick) {
            return <TitleLink aria-label={linkText} onClick={onLinkClick} {...linkProps} />;
        }
        return null;
    };

    const linkElement = linkComponent();
    if (!React.Children.count(children) && !linkElement) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...otherProps}>
            <Typography {...styles.title}>{children}</Typography>
            {linkElement}
        </StyledWrapper>
    );
};

export default TitleTypography;
